<template>
    <div
        v-if="!allTasksClaimed"
        :class="$style.componentContainer"
        data-interface-section="tasks-widget"
    >
        <div
            :class="[$style.tasksWidget, { [$style.rotateOut]: hide, [$style.rotateIn]: show }]"
            @animationend="onAnimationEnd"
        >
            <TaskWidgetPlaceholder v-if="isLoading" />

            <template v-else-if="isAuthorized">
                <template v-if="!tasksError">
                    <TaskWidget
                        :task="task"
                        :disabled="isClaimStarted"
                        @claimTask="onTaskClaim"
                        @taskStart="onTaskStart"
                        @taskClick="onTaskClick"
                    />
                </template>

                <div
                    v-else
                    :class="$style.errorContainer"
                >
                    <Typography
                        type="paragraph"
                        size="xs"
                    >
                        {{ $tr('tasks.error_title') }}
                    </Typography>
                    <Button
                        color="carbon-400"
                        visual="fill"
                        interfaceTarget="reload-tasks"
                        @click="tasksStore.onReloadClick"
                    >
                        {{ $tr('tasks.error_button') }}
                    </Button>
                </div>
            </template>
            <template v-else>
                <TaskWidget isRegister />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'

import Button from '@/components_new/Button.vue'
import TaskWidget from '@/components_new/TaskItems/TaskWidget.vue'
import TaskWidgetPlaceholder from '@/components_new/TaskItems/TaskWidgetPlaceholder.vue'
import { useTasksStore } from '@/components_new/TaskItems/store'
import Typography from '@/components_new/Typography.vue'
import { ROUTE_NAMES } from '@/router/constants'
import { useUserStore } from '@/store/user-store/user-store'
import { TaskDto } from '@/types'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

const hide = ref(false)
const show = ref(false)
const isMounted = useIsMounted()

const tasksStore = useTasksStore()
const userStore = useUserStore()
const { tasksError, allTasksClaimed, firstTask, isClaimStarted } = storeToRefs(tasksStore)

const task = ref<TaskDto>()

onMounted(() => {
    if (!task.value && firstTask.value) {
        task.value = firstTask.value
    }
})

function onAnimationEnd() {
    if (hide.value) {
        task.value = firstTask.value
        hide.value = false
        show.value = true
    }
}

watch(firstTask, (newFirstTask, oldFirstTask) => {
    if (!task.value) {
        task.value = newFirstTask
        return
    }

    if (oldFirstTask?.task_id === newFirstTask?.task_id && oldFirstTask?.status === newFirstTask?.status) {
        return
    }

    hide.value = true
    show.value = false
})

const { isAuthorized, isUserInfoLoaded } = storeToRefs(userStore)

const isLoading = computed(
    () =>
        !isMounted.value ||
        // User info not loaded yet
        !isUserInfoLoaded.value ||
        // Logged in but tasks not fetched yet
        (isAuthorized.value && !task.value),
)

const router = useRouter()

const onTaskClaim = (incomingTask: TaskDto) => {
    tasksStore.onTaskClaimed(incomingTask)
}

const onTaskClick = () => {
    router.push({ name: ROUTE_NAMES.PROFILE })
}
const onTaskStart = ({ taskId, link }: { taskId: string; link: RouteLocationRaw }) => {
    tasksStore.onTaskStart(taskId)
    router.push(link)
}
</script>

<style module>
.tasksWidget {
    border-radius: 26px;
    background-color: var(--carbon-400);
    padding: 4px;
    transition:
        transform 0.5s,
        box-shadow 0.3s;
    cursor: pointer;
}

.componentContainer {
    min-width: 220px;
    perspective: 200px;
}

.componentContainer:hover > .tasksWidget,
.componentContainer:focus > .tasksWidget {
    outline: 0.5px solid var(--purple-300);
    background: var(--carbon-300);
    box-shadow: 0 0 30px 0 rgba(163, 109, 253, 0.64) inset;
}

.errorContainer {
    display: flex;
    justify-content: center;
    flex-direction: row;
    align-items: center;
    text-align: center;
    border-radius: 26px;
    padding: 0;
    gap: 8px;
    height: 36px;
}

@keyframes rotateOut {
    0% {
        transform: rotate3d(1, 0, 0, 0);
    }

    100% {
        transform: rotate3d(1, 0, 0, 90deg);
    }
}

@keyframes rotateIn {
    0% {
        transform: rotate3d(1, 0, 0, 270deg);
    }

    100% {
        transform: rotate3d(1, 0, 0, 360deg);
    }
}

.rotateOut {
    animation: rotateOut 0.5s forwards;
}

.rotateIn {
    animation: rotateIn 0.5s forwards;
}

@media (--desktop-medium-plus) {
    .componentContainer {
        min-width: 300px;
    }
}
</style>
